import React from 'react';

/**
 * Password strength indicator component
 * @param {string} password - The password to check
 * @returns {JSX.Element} - The password strength indicator component
 */
const PasswordStrengthIndicator = ({ password }) => {
  // Check if password meets requirements
  const hasMinLength = password.length >= 8;
  const hasUppercase = /[A-Z]/.test(password);
  const hasLowercase = /[a-z]/.test(password);
  const hasNumber = /[0-9]/.test(password);
  const hasSymbol = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password);

  // Calculate strength
  const requirements = [hasMinLength, hasUppercase, hasLowercase, hasNumber, hasSymbol];
  const meetsAllRequirements = requirements.every(Boolean);
  const strengthPercentage = (requirements.filter(Boolean).length / requirements.length) * 100;

  // Determine color based on strength
  let barColor = 'bg-red-500';
  let strengthText = 'Weak';
  
  if (meetsAllRequirements) {
    barColor = 'bg-green-500';
    strengthText = 'Strong';
  } else if (strengthPercentage >= 60) {
    barColor = 'bg-yellow-500';
    strengthText = 'Medium';
  }

  return (
    <div className="mt-1 mb-4">
      <div className="flex justify-between mb-1">
        <span className="text-xs font-medium text-gray-700">Password Strength: {strengthText}</span>
      </div>
      <div className="w-full bg-gray-200 rounded-full h-2.5">
        <div 
          className={`${barColor} h-2.5 rounded-full transition-all duration-300`} 
          style={{ width: `${strengthPercentage}%` }}
        ></div>
      </div>
      
      <div className="mt-2 space-y-1">
        <p className="text-xs text-gray-600">Password must:</p>
        <ul className="text-xs space-y-1 pl-5 list-disc">
          <li className={hasMinLength ? 'text-green-600' : 'text-gray-600'}>
            Be at least 8 characters long
          </li>
          <li className={hasUppercase ? 'text-green-600' : 'text-gray-600'}>
            Include at least one uppercase letter
          </li>
          <li className={hasLowercase ? 'text-green-600' : 'text-gray-600'}>
            Include at least one lowercase letter
          </li>
          <li className={hasNumber ? 'text-green-600' : 'text-gray-600'}>
            Include at least one number
          </li>
          <li className={hasSymbol ? 'text-green-600' : 'text-gray-600'}>
            Include at least one symbol
          </li>
        </ul>
      </div>
    </div>
  );
};

export default PasswordStrengthIndicator;